import { useEffect, useRef, useState } from 'react';

function usePrev(value) {
  const ref = useRef();
  
  useEffect(() => {
    ref.current = value;
  }, [value]);
  
  return ref.current;
}

function useOnClickOutside(ref, handler) {
  useEffect(
    () => {
      const listener = (event) => {
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);

      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    [ref, handler],
  );
}

function useNetworkState() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

function useIsMapLoaded(map) {
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  useEffect(() => {
    if (map) {
      if (map.isStyleLoaded()) {
        setIsMapLoaded(true);
      } else {
        map.once('load', () => setIsMapLoaded(true));
      }
    }
  }, [map]);

  return isMapLoaded;
}

function useMapBounds(map) {
  const [mapBounds, setMapBounds] = useState(null);

  useEffect(() => {
    const updateBounds = () => {
      const b = map.getBounds();
      setMapBounds({
        minLng: b.getWest(),
        minLat: b.getSouth(),
        maxLng: b.getEast(),
        maxLat: b.getNorth(),
      });
    };

    if (map) {
      map.on('load', updateBounds);
      map.on('moveend', updateBounds);

      updateBounds();
    }
    return () => {
      if (map) {
        map.off('load', updateBounds);
        map.off('moveend', updateBounds);
      }
    };
  }, [map]);

  return mapBounds;
}

export {
  usePrev,
  useOnClickOutside,
  useNetworkState,
  useIsMapLoaded,
  useMapBounds,
};